import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import websocketConnection from '../../websocket';
import { showRequestError } from '../../utils/requestError';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { trackSocialGraphEvent } from '../../pages/SocialGraphPOC/analytics';
import {
  CaseGraphResponse,
  SavedGraphDTO,
  SavedGraphDetailDTO,
  SavedGraphSourceType,
  SocialGraphModesResponse,
  parseApiDate,
  socialGraphApi,
  toApiDate,
} from '../../utils/socialGraphApi';
import { GraphWidgetStats, GraphWidgetStatus } from './GraphWidget';

// WS status pushes are best-effort (personal channel, dropped on reconnect), so a
// slow task poll backs them up while a generation is in flight.
const FALLBACK_POLL_MS = 15000;

// The list endpoint filters by source server-side; newest first, so the first
// row is "the" graph for this source entity.
async function findSavedGraph(
  sourceType: SavedGraphSourceType,
  sourceId: number,
  signal?: AbortSignal,
): Promise<SavedGraphDTO | null> {
  const list = await socialGraphApi.listGraphs(
    { page: 1, size: 1, source_type: sourceType, source_id: sourceId },
    signal,
  );
  return list.objects[0] ?? null;
}

export interface SavedGraphGenerateOptions {
  // blank/omitted → backend derives a name from the source
  name?: string;
  // analysis window — actor-kind graphs only, whole days inclusive
  startDate?: Date | null;
  endDate?: Date | null;
}

// The list row carries the four numbers once the backend summarises them
// (scaling plan 1.7); until then they are absent and the detail is fetched.
function statsFromRow(row: SavedGraphDTO): GraphWidgetStats | null {
  const { nodes_count, edges_count, communities_count, bridging_actors_count } = row;
  if (nodes_count == null || edges_count == null || communities_count == null || bridging_actors_count == null) {
    return null;
  }
  return {
    actors: nodes_count,
    relations: edges_count,
    communities: communities_count,
    bridgingActors: bridging_actors_count,
  };
}

function computeStats(detail: SavedGraphDetailDTO): GraphWidgetStats | null {
  if (!detail.result) return null;
  if (detail.kind === 'actor') {
    const graph = (detail.result as SocialGraphModesResponse).exploratory;
    if (!graph) return null;
    return {
      actors: graph.global_metrics.nodes_count,
      relations: graph.global_metrics.edges_count,
      communities: graph.clusters.length,
      bridgingActors: graph.nodes.filter((node) => node.actor_role === 'Bridge').length,
    };
  }
  const graph = detail.result as CaseGraphResponse;
  // Case graphs have no Bridge role; Mixed nodes (both produce and amplify) are the
  // closest "relays content between actors" notion — pending a design decision.
  return {
    actors: graph.global_metrics.nodes_count,
    relations: graph.global_metrics.edges_count,
    communities: graph.clusters.length,
    bridgingActors: graph.nodes.filter((node) => node.role === 'Mixed').length,
  };
}

// Owns the saved graph for one source entity (case / topic event / actor group):
// lookup, generate/cancel/regenerate, live status via WS + fallback polling, and
// the ready-state stats. Drives the presentational GraphWidget on host pages.
export function useSavedGraph(
  sourceType: SavedGraphSourceType,
  sourceId: number | string | null | undefined,
  // pass the social_graph feature-flag check here: the backend answers 403 for
  // flag-less workspaces and API.fetch treats any 403 as an expired session
  // (token wipe + redirect), so the hook must not fire requests at all then
  enabled = true,
) {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const id = !enabled || sourceId == null || sourceId === '' ? null : Number(sourceId);

  const [graph, setGraph] = useState<SavedGraphDTO | null>(null);
  const [lookupDone, setLookupDone] = useState(false);
  const [stats, setStats] = useState<GraphWidgetStats | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  // bumping re-runs the lookup effect (after generate/cancel and on WS terminal states)
  const [refreshSeq, setRefreshSeq] = useState(0);
  const refresh = useCallback(() => setRefreshSeq((seq) => seq + 1), []);

  useEffect(() => {
    setGraph(null);
    setStats(null);
    setLookupDone(false);
    if (id === null) return;
    const controller = new AbortController();
    findSavedGraph(sourceType, id, controller.signal)
      .then((match) => {
        setGraph(match);
        setLookupDone(true);
      })
      .catch((error) => {
        showRequestError(error, t('Graphs'));
        setLookupDone(true);
      });
    return () => controller.abort();
  }, [sourceType, id, refreshSeq, t]);

  // Live status updates: the worker pushes SOCIAL_GRAPH messages to the creator.
  // The graph overlay listens to the same type while it's open, so cleanup must
  // remove exactly this handler (registry supports several per type).
  useEffect(() => {
    if (id === null) return;
    const handleUpdate = (message: {
      object_id?: number;
      data?: Partial<SavedGraphDTO>;
    }) => {
      const data = message?.data;
      if (!data) return;
      setGraph((prev) => {
        // besides the source match, accept pushes for the graph already tracked —
        // robustness for push payloads that omit the source labels
        const matchesSource = data.source_type === sourceType && data.source_id === id;
        const matchesTracked = prev !== null && data.id === prev.id;
        if (!matchesSource && !matchesTracked) return prev;
        // keep fields the push doesn't carry (user) when it's the same graph
        if (prev && prev.id === data.id) return { ...prev, ...data };
        return { user: null, ...data } as SavedGraphDTO;
      });
    };
    websocketConnection.addHandler('SOCIAL_GRAPH', handleUpdate);
    return () => websocketConnection.removeHandler('SOCIAL_GRAPH', handleUpdate);
  }, [sourceType, id]);

  // Fallback poll while a generation is in flight. Errors are swallowed on purpose:
  // this is a redundant safety net behind the WS channel and a toast every 15s
  // would spam the page if the network blips.
  const kind = graph?.kind;
  const taskId =
    graph && (graph.status === 'queued' || graph.status === 'generating') ? graph.task_id : null;
  useEffect(() => {
    if (!taskId || !kind) return;
    const interval = setInterval(() => {
      const poll = kind === 'actor' ? socialGraphApi.pollActors : socialGraphApi.pollCase;
      poll(taskId)
        .then((job) => {
          if (job.status !== 'PENDING' && job.status !== 'RUNNING') refresh();
        })
        .catch(() => refresh()); // 404 = graph deleted/cancelled elsewhere — re-sync
    }, FALLBACK_POLL_MS);
    return () => clearInterval(interval);
  }, [taskId, kind, refresh]);

  // The 4 ready-state stats come from the list row when the backend summarises
  // them; otherwise they only exist inside the stored result, which means
  // downloading the whole graph (tens of MB on large graphs) for four numbers —
  // fetched once the row is ready, and only then.
  const readyGraphId = graph?.status === 'ready' ? graph.id : null;
  const rowStats = graph?.status === 'ready' ? statsFromRow(graph) : null;
  const hasRowStats = rowStats !== null;
  useEffect(() => {
    setStats(rowStats);
    if (readyGraphId === null || hasRowStats) return;
    const controller = new AbortController();
    socialGraphApi
      .getGraph(readyGraphId, controller.signal)
      .then((detail) => setStats(computeStats(detail)))
      .catch((error) => showRequestError(error, t('Graph')));
    return () => controller.abort();
    // rowStats is derived from `graph`; keying on the id + presence keeps the
    // effect from re-running (and re-fetching) on unrelated row updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyGraphId, hasRowStats, t]);

  const generate = useCallback(async (options?: SavedGraphGenerateOptions) => {
    if (id === null) return;
    setIsBusy(true);
    try {
      const name = options?.name?.trim() || undefined;
      if (sourceType === 'actor') {
        // single-actor graphs go through the dedicated explore endpoint (the
        // actor + their strongest neighbours); same task handle as analyze-actors
        await socialGraphApi.enqueueExploreActor({
          actor_id: id,
          name,
          start_date: toApiDate(options?.startDate),
          end_date: toApiDate(options?.endDate),
        });
      } else if (sourceType === 'actor_group') {
        await socialGraphApi.enqueueActors({
          actor_group_ids: [id],
          name,
          start_date: toApiDate(options?.startDate),
          end_date: toApiDate(options?.endDate),
        });
      } else if (sourceType === 'case') {
        await socialGraphApi.enqueueCase({ narrative_id: id, name });
      } else if (sourceType === 'threat') {
        // threats are case graphs scoped to a threat instead of a narrative
        await socialGraphApi.enqueueCase({ threat_id: id, name });
      } else {
        await socialGraphApi.enqueueCase({ event_id: id, name });
      }
      trackSocialGraphEvent(
        'sg_graph_generate_requested',
        {
          mode: 'generate',
          source_type: sourceType,
          source_id: id,
          has_date_range: Boolean(options?.startDate || options?.endDate),
        },
        currentUser,
      );
      // enqueue returns only a task handle — refetch the list to pick up the new row
      refresh();
    } catch (error) {
      showRequestError(error, t('Graph'));
    } finally {
      setIsBusy(false);
    }
  }, [sourceType, id, refresh, t, currentUser]);

  const cancel = useCallback(async () => {
    if (!graph?.task_id) return;
    setIsBusy(true);
    try {
      if (graph.kind === 'actor') {
        await socialGraphApi.cancelActors(graph.task_id);
      } else {
        await socialGraphApi.cancelCase(graph.task_id);
      }
      trackSocialGraphEvent(
        'sg_graph_generation_cancelled',
        { graph_id: graph.id, graph_kind: graph.kind, source_type: graph.source_type },
        currentUser,
      );
      // a cancelled first run deletes the graph (back to idle); a cancelled
      // regeneration reverts to ready — refetch to learn which
      refresh();
    } catch (error) {
      showRequestError(error, t('Graph'));
    } finally {
      setIsBusy(false);
    }
  }, [graph, refresh, t, currentUser]);

  const regenerate = useCallback(async (options?: SavedGraphGenerateOptions) => {
    if (!graph) return;
    setIsBusy(true);
    try {
      // one call does it all: a non-blank name renames, and explicit date fields
      // replace the analysis window (nulls clear it back to all time, while an
      // OMITTED field leaves the stored window untouched — don't collapse the
      // two). Date fields only for actor graphs — a window on a case graph is
      // a 400.
      const name = options?.name?.trim();
      const updated = await socialGraphApi.regenerateGraph(
        graph.id,
        options
          ? {
              ...(graph.kind === 'actor' && options.startDate !== undefined
                ? { start_date: toApiDate(options.startDate) ?? null }
                : {}),
              ...(graph.kind === 'actor' && options.endDate !== undefined
                ? { end_date: toApiDate(options.endDate) ?? null }
                : {}),
              ...(name && name !== graph.name ? { name } : {}),
            }
          : undefined,
      );
      setGraph(updated);
      trackSocialGraphEvent(
        'sg_graph_generate_requested',
        {
          mode: 'regenerate',
          graph_id: graph.id,
          graph_kind: graph.kind,
          source_type: graph.source_type,
          renamed: Boolean(name && name !== graph.name),
          has_date_range: Boolean(options?.startDate || options?.endDate),
        },
        currentUser,
      );
    } catch (error) {
      showRequestError(error, t('Graph'));
    } finally {
      setIsBusy(false);
    }
  }, [graph, t, currentUser]);

  const status: GraphWidgetStatus = useMemo(() => {
    if (!graph) return 'idle';
    if (graph.status === 'ready') return 'ready';
    if (graph.status === 'failed') return 'failed';
    return 'generating'; // queued + generating look the same in the widget
  }, [graph]);

  return {
    status,
    graphId: graph?.id ?? null,
    generatedAt: graph?.created_at ?? null,
    // current name + analysis window, for prefilling the regenerate modal
    graphName: graph?.name ?? null,
    startDate: parseApiDate(graph?.start_date),
    endDate: parseApiDate(graph?.end_date),
    stats,
    // before the lookup settles the widget briefly shows idle — keep its buttons
    // disabled so a click can't enqueue a duplicate graph
    isBusy: isBusy || !lookupDone,
    generate,
    cancel,
    regenerate,
  };
}
